import { useContactUsEmailStorage } from "@/components/support/contactUs/email/contact-us-email-storage";
import { cn } from "@/libs/utils";
import { ContactUsSchemaType } from "@/schemas/contact-us";
import { useTrans } from "@/utils/useTrans";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/v2-components/ui/form";
import ChevronDownIcon from "@/v2-components/core/icon/chevron-down-icon";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from "@/v2-components/ui/select";
import { Fragment, useEffect } from "react";
import { useFormContext } from "react-hook-form";

export const platforms = [
  {
    id: "windows",
    label: "Windows PC",
    categories: ["device", "game-software"],
    topics: ["pair-connect", "motor-vibration", "haptic-feedback", "sdk"],
  },
  {
    id: "quest",
    label: "Meta Quest",
    categories: ["device", "game-software"],
    topics: ["pair-connect", "motor-vibration", "haptic-feedback", "sdk"],
  },
  {
    id: "psvr2",
    label: "PSVR2",
    categories: ["device"],
    topics: ["pair-connect", "motor-vibration"],
  },
  {
    id: "mobile",
    label: "Mobile",
    categories: ["game-software"],
    topics: ["pair-connect", "haptic-feedback", "sdk"],
  },
];

export default function PlatformSelect() {
  const { t } = useTrans(["support"]);

  const contactUsEmailStorage = useContactUsEmailStorage();

  const form = useFormContext<ContactUsSchemaType>();
  const currentCategory = form.watch("category");
  const currentTopic = form.watch("topic");

  const options = platforms.filter((platform) => {
    const category = platform.categories.some((category) =>
      currentCategory.includes(category),
    );
    const topic = platform.topics.some((topic) => currentTopic === topic);
    return category && topic;
  });

  if (options.length === 0) return null;

  return (
    <FormField
      control={form.control}
      name="platform"
      render={({ field }) => (
        <FormItem className="space-y-2">
          <FormLabel variant="select">
            <span>{t("Platform")}</span>
            <span>{" *"}</span>
          </FormLabel>
          <Select
            onValueChange={(value) => {
              field.onChange(value);
              contactUsEmailStorage.set(form.getValues());
            }}
            value={field.value}
          >
            <FormControl>
              <SelectTrigger variant="form" ref={field.ref}>
                <SelectValue placeholder={t("Choose a platform")} />
                <ChevronDownIcon />
              </SelectTrigger>
            </FormControl>
            <SelectContent>
              {options.map((topic, index) => (
                <Fragment key={topic.id}>
                  <SelectItem value={topic.id}>{topic.label}</SelectItem>
                  <SelectSeparator
                    className={cn(index === options.length - 1 && "hidden")}
                  />
                </Fragment>
              ))}
            </SelectContent>
          </Select>
          <FormMessage t={t} />
        </FormItem>
      )}
    />
  );
}
